import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { useToast } from '../ui/Toast';
import { Icon } from '../ui/Icon';

interface CompanyItem {
  id: string;
  nome: string;
  slug: string;
}

/**
 * Seletor de Workspace (impersonate) — F-07.
 * Apenas super_admin. Permite "entrar" no workspace de uma empresa (o backend
 * passa a escopar Files/Logs/Dashboard a ela via effectiveCompanyId) e sair.
 * Autocontido: busca a lista de empresas e consome impersonate do AuthProvider.
 */
export function WorkspaceSwitcher() {
  const { user, impersonate, setImpersonate, apiFetch } = useAuth();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<CompanyItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const isSuper = user?.papel === 'super_admin';

  const load = useCallback(async () => {
    if (loaded || loading) return;
    setLoading(true);
    const r = await apiFetch<{ items: CompanyItem[] }>('/v1/admin/companies/');
    setLoading(false);
    setLoaded(true);
    if (r.ok && r.data) setItems(r.data.items);
  }, [apiFetch, loaded, loading]);

  // Fecha ao clicar fora / Esc
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!isSuper) return null;

  async function enter(c: CompanyItem) {
    if (busy) return;
    setBusy(true);
    await setImpersonate(c.id);
    setBusy(false);
    setOpen(false);
    toast(`Entrou no workspace de ${c.nome}`, 'info');
  }

  async function exit() {
    if (busy) return;
    setBusy(true);
    await setImpersonate(null);
    setBusy(false);
    setOpen(false);
    toast('Você voltou à visão de super admin', 'info');
  }

  const active = impersonate;

  return (
    <div ref={rootRef} style={{ position: 'relative', marginBottom: 8 }}>
      <div className="eyebrow" style={{ padding: '0 10px 6px' }}>Workspace</div>
      <button
        onClick={() => {
          setOpen((o) => !o);
          void load();
        }}
        aria-expanded={open}
        aria-haspopup="listbox"
        title={active ? `Vendo como ${active.nome}` : 'Selecionar workspace de empresa'}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 9,
          width: '100%',
          padding: '8px 10px',
          borderRadius: 'var(--r-sm)',
          border: '1px solid var(--border)',
          background: active ? 'var(--amber-tint, var(--bg-2))' : 'var(--bg-1)',
          color: 'var(--text-1)',
          fontSize: 13,
          fontWeight: 500,
          textAlign: 'left',
          cursor: 'pointer'
        }}
        onMouseEnter={(e) => (e.currentTarget.style.borderColor = 'var(--brand)')}
        onMouseLeave={(e) => (e.currentTarget.style.borderColor = 'var(--border)')}
      >
        <Icon name="building" size={16} style={{ color: active ? 'var(--amber-strong)' : 'var(--text-2)' }} />
        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {active ? active.nome : 'Super admin (geral)'}
        </span>
        <Icon
          name="chevDown"
          size={14}
          style={{
            color: 'var(--text-3)',
            transform: open ? 'rotate(180deg)' : 'none',
            transition: 'transform 0.15s'
          }}
        />
      </button>

      {open && (
        <div
          className="anim-scale"
          role="listbox"
          style={{
            position: 'absolute',
            bottom: 'calc(100% + 6px)',
            left: 0,
            right: 0,
            maxHeight: 320,
            overflowY: 'auto',
            background: 'var(--bg-1)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--r-md)',
            boxShadow: 'var(--shadow-2)',
            padding: 6,
            zIndex: 70
          }}
        >
          <button
            onClick={exit}
            disabled={!active || busy}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 9,
              width: '100%',
              padding: '8px 10px',
              borderRadius: 'var(--r-sm)',
              border: 'none',
              background: !active ? 'var(--brand-tint)' : 'transparent',
              color: !active ? 'var(--brand-strong)' : 'var(--text-1)',
              fontSize: 13,
              fontWeight: !active ? 600 : 500,
              textAlign: 'left',
              cursor: !active || busy ? 'default' : 'pointer'
            }}
            onMouseEnter={(e) => {
              if (active && !busy) e.currentTarget.style.background = 'var(--bg-2)';
            }}
            onMouseLeave={(e) => {
              if (active) e.currentTarget.style.background = 'transparent';
            }}
          >
            <Icon name="dashboard" size={15} style={{ color: 'var(--text-2)' }} />
            <span style={{ flex: 1 }}>Super admin (geral)</span>
            {!active && <Icon name="check" size={14} style={{ color: 'var(--brand)' }} />}
          </button>

          <div style={{ height: 1, background: 'var(--border-soft)', margin: '6px 4px' }} />

          {loading && (
            <div style={{ padding: '8px 10px', fontSize: 12.5, color: 'var(--text-3)' }}>Carregando…</div>
          )}
          {!loading && items.length === 0 && (
            <div style={{ padding: '8px 10px', fontSize: 12.5, color: 'var(--text-3)' }}>
              Nenhuma empresa.
            </div>
          )}
          {items.map((c) => {
            const sel = active?.company_id === c.id;
            return (
              <button
                key={c.id}
                role="option"
                aria-selected={sel}
                onClick={() => enter(c)}
                disabled={busy}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 9,
                  width: '100%',
                  padding: '8px 10px',
                  borderRadius: 'var(--r-sm)',
                  border: 'none',
                  background: sel ? 'var(--brand-tint)' : 'transparent',
                  color: sel ? 'var(--brand-strong)' : 'var(--text-1)',
                  fontSize: 13,
                  fontWeight: sel ? 600 : 500,
                  textAlign: 'left',
                  cursor: busy ? 'default' : 'pointer'
                }}
                onMouseEnter={(e) => {
                  if (!sel && !busy) e.currentTarget.style.background = 'var(--bg-2)';
                }}
                onMouseLeave={(e) => {
                  if (!sel) e.currentTarget.style.background = 'transparent';
                }}
              >
                <Icon name="building" size={15} style={{ color: 'var(--text-2)' }} />
                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {c.nome}
                </span>
                {sel && <Icon name="check" size={14} style={{ color: 'var(--brand)' }} />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
